import React from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  useMessages,
} from "@tiny/components";
import { CircularProgress } from "@mui/material";
import Download from "@tiny/icons/generated/Download";
import {
  downloadFileFromUrl,
  formatUploadedAt,
} from "../SuppressionListRow.helper";
import {
  extractBackendError,
  getErrorMessage,
} from "@tiny/components/src/utils/errorUtils";
import { showUploadMessage } from "@tiny/components/src/Snackbar/snackbar-helpers";
import { closeSnackbar } from "notistack";
import { useApi } from "@tiny/api";
import { SuppressionFilesDialogProps } from "./SuppressionFilesDialog.types";
import { useStyles } from "./SuppressionFilesDialog.styles";

function SuppressionFilesDialog(props: SuppressionFilesDialogProps) {
  const { open, onClose, files, suppressionId } = props;
  const classes = useStyles();
  const { showErrorMessage } = useMessages();
  const api = useApi();

  const [zipLoading, setZipLoading] = React.useState(false);

  const sortedFiles = React.useMemo(
    () => [...files].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)),
    [files]
  );

  const handleDownload = React.useCallback(
    async (url?: string, fileName?: string) => {
      if (!url) {
        return;
      }
      try {
        await downloadFileFromUrl(url, fileName || "suppression.csv");
      } catch (e: any) {
        extractBackendError(e?.response).then((be) => {
          showErrorMessage(getErrorMessage(be));
        });
      }
    },
    [showErrorMessage]
  );

  const handleDownloadAll = React.useCallback(async () => {
    try {
      setZipLoading(true);
      showUploadMessage({
        variant: "uploading",
        message: "Preparing download",
        description: "Your suppression files are being zipped",
        persist: true,
        showClose: true,
      });

      const { data } = await api.getSuppressionFilesZip(suppressionId);
      await downloadFileFromUrl(data?.url || "", "suppressions.zip");

      closeSnackbar();
      showUploadMessage({
        variant: "success",
        message: "Downloaded successfully!",
        description:
          "Your suppression files have been downloaded successfully!",
        primaryCtaActionText: "Close",
        primaryCtaActionFunction: () => {
          closeSnackbar();
        },
        persist: false,
      });
    } catch (e: any) {
      closeSnackbar();
      extractBackendError(e?.response).then((be) => {
        showErrorMessage(getErrorMessage(be));
      });
    } finally {
      setZipLoading(false);
    }
  }, [api, suppressionId, showErrorMessage]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle onClose={onClose}>Download suppression files</DialogTitle>
      <DialogContent>
        <div className={classes.header}>
          <Button
            variant="outlined"
            size="small"
            startIcon={
              zipLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Download />
              )
            }
            onClick={handleDownloadAll}
            disabled={zipLoading}
          >
            Download all files
          </Button>
        </div>
        {sortedFiles.map((file, index) => (
          <div
            className={classes.row}
            key={`${file.url}-${index}`}
            onClick={() => handleDownload(file.url, file.fileName)}
          >
            <Typography variant="body2" color="grey" tone={900}>
              Uploaded at{" "}
              <Typography
                component="span"
                variant="body2"
                color="grey"
                tone={900}
                fontWeight="bold"
              >
                {formatUploadedAt(file.createdAt)}
              </Typography>
            </Typography>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleDownload(file.url, file.fileName);
              }}
            >
              <Download />
            </IconButton>
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
}

export default SuppressionFilesDialog;
